<template>
  <ion-page id="subscribe-page">

    <AlertReplacement :alertInfo="alertInfo" :trigger="alertTrigger"></AlertReplacement>

    <ion-content :fullscreen="true">
      <div id="page" :class="{ 'text-dark' : isDark,'text-light' : !isDark }" :style="styleSubscribe" :key="menuTrigger">
        <div v-show="showState == SHOW_BLURB">

          <!-- Hero -->
          <section class="hero">
            <h1 class="hero-title">{{ t("Subscribing adds so much more!") }}</h1>
            <p class="hero-sub">{{ t("A lot of ChessVis is free — and always will be. A subscription unlocks the deeper tools for serious improvement.") }}</p>
            <!-- <ion-button expand="block" class="hero-cta" @click="handleSubscribe">
              {{ t("Verify Your Email to Subscribe") }}
            </ion-button> -->
          </section>

          <!-- top line message -->
          <section v-if="topLineString.length > 0" class="feature-section">
              <h2 class="top-line-note">{{ topLineString }}</h2>
          </section>

          <!-- What's free -->
          <section class="feature-section">
            <div class="section-header free-header">
              <h2 class="section-title">{{ t("Lots of ChessVis is Free") }}</h2>
            </div>

            <div class="feature-card">
              <h3>{{ t("Tactics Gym — Puzzles and Visualization Exercises - Completely Free") }}</h3>
              <p>{{ t("All puzzles including regular and visualized puzzles. Timed, offline and blindfold puzzles are now free. Who's Guarding Whom, Move Following, Static Boards and all visualization tools. Blindfold chess at the introductory stepping-stone level. No subscription needed for any of it.") }}</p>
            </div>

            <div class="feature-card">
              <h3>{{ t("Opening Explorer — Free") }}</h3>
              <p>{{ t("The Opening Explorer with its 200 million equal-rating Lichess Rapid game collection is free. This unique collection only includes games where both players are within the same 50-point rating bracket — so you're studying players who actually play like you.") }}</p>
            </div>

          </section>

          <!-- Subscriber benefits overview -->
          <section class="feature-section">
            <div class="section-header subscriber-header">
              <h2 class="section-title">{{ t("Subscriber Benefits") }}</h2>
            </div>

            <div class="benefits-list">
               <div class="benefit-item">
                <ion-icon :icon="checkmarkCircleOutline" class="benefit-icon" aria-hidden="true" />
                <span>{{ t("AI-Powered Opening Explorer") }}</span>
              </div>
              <div class="benefit-item">
                <ion-icon :icon="checkmarkCircleOutline" class="benefit-icon" aria-hidden="true" />
                <span>{{ t("Opening preparation with repertoires") }}</span>
              </div>
              <div class="benefit-item">
                <ion-icon :icon="checkmarkCircleOutline" class="benefit-icon" aria-hidden="true" />
                <span>{{ t("Analyze your games") }}</span>
              </div>
              <div class="benefit-item">
                <ion-icon :icon="checkmarkCircleOutline" class="benefit-icon" aria-hidden="true" />
                <span>{{ t("Advanced blindfold chess") }}</span>
              </div>
              <!-- <div class="benefit-item">
                <ion-icon :icon="checkmarkCircleOutline" class="benefit-icon" aria-hidden="true" />
                <span>{{ t("ChessVis AI monthly credits") }}</span>
              </div> -->
              <!-- <div class="benefit-item">
                <ion-icon :icon="checkmarkCircleOutline" class="benefit-icon" aria-hidden="true" />
                <span>{{ t("Play Friend — coming soon") }}</span>
              </div> -->
            </div>
          </section>

          <!-- Opening Mastery -->
          <section class="feature-section">
            <div class="section-header">
              <div class="section-icon-wrap purple">
                <ion-icon :icon="trophyOutline" aria-hidden="true" />
              </div>
              <h2 class="section-title">{{ t("Opening Mastery") }}</h2>
            </div>

              <div class="feature-card highlight">
                <div class="feature-card-label">{{ t("AI-Powered Opening Explorer") }}</div>
                <h3>{{ t("Let AI Make Sense of the Data") }}</h3>
                <p>{{ t("The numbers tell a story — AI reads it for you. Get recommendations on the moves to consider and the moves to avoid, explained in plain language, based on how players at your rating actually perform.") }}</p>
              </div>
            
            <div class="feature-card">
              <h3>{{ t("Build Your Repertoire") }}</h3>
              <p>{{ t("Subscribers can build their repertoire library. The unique focus here is on the moves you'll actually see — not long move sequences that are rarely played. Have you seen the videos in the Account section?") }}</p>
            </div>

            <div class="feature-card">
              <h3>{{ t("Interactive Repertoire Builder") }}</h3>
              <p>{{ t("Build your opening lines move by move, informed by the Opening Explorer data you already have.") }}</p>
            </div>

            <div class="feature-card">
              <h3>{{ t("Tree View") }}</h3>
              <p>{{ t("See your entire repertoire as a branching tree — understand where your lines diverge and how deep your preparation actually goes.") }}</p>
            </div>

            <div class="feature-card">
              <h3>{{ t("Drill — Spaced Repetition") }}</h3>
              <p>{{ t("Daily drills using spaced repetition, so the moves you struggle with come back more often and the ones you know stay sharp with less effort.") }}</p>
            </div>

            <div class="feature-card highlight">
              <div class="feature-card-label">Where repertoires usually fail</div>
              <h3>{{ t("Practice Into the Middle Game") }}</h3>
              <p>{{ t("Extends your play beyond your prepared lines into the middle game, surfacing the positions that leave you thinking 'What Do I Do Now???' That's where real preparation happens.") }}</p>
            </div>

          </section>

          <!-- Analyze My Games -->
          <section class="feature-section">
            <div class="section-header">
              <div class="section-icon-wrap purple">
                <ion-icon :icon="analyticsOutline" aria-hidden="true" />
              </div>
              <h2 class="section-title">{{ t("Analyze My Games") }}</h2>
            </div>

            <div class="feature-card">
              <h3>{{ t("Download & Analyze") }}</h3>
              <p>{{ t("Download all your games from chess.com and lichess.org and run them through the Opening Explorer. See exactly where you're deviating from your preparation and what the data says about the positions you're reaching.") }}</p>
            </div>

          </section>

          <!-- Blindfold Chess -->
          <section class="feature-section">
            <div class="section-header">
              <div class="section-icon-wrap teal">
                <ion-icon :icon="eyeOffOutline" aria-hidden="true" />
              </div>
              <h2 class="section-title">{{ t("Advanced Blindfold Chess") }}</h2>
            </div>

            <div class="feature-card">
              <h3>{{ t("Full Blindfold Play") }}</h3>
              <p>{{ t("Blindfold chess against Stockfish, Komodo and Maia engines at all levels and all stepping stones. The introductory level is free — subscribers unlock the full progression.") }}</p>
            </div>

          </section>

          <!--
          ChessVis AI — coming soon
          <section class="feature-section">
            <div class="section-header">
              <div class="section-icon-wrap ai">
                <ion-icon :icon="sparklesOutline" aria-hidden="true" />
              </div>
              <h2 class="section-title">ChessVis AI</h2>
            </div>

            <div class="feature-card highlight">
              <div class="feature-card-label">Included with subscription</div>
              <h3>{{ t("Monthly AI Credits") }}</h3>
              <p>{{ t("Subscribers receive monthly ChessVis AI credits to use directly inside ChessVis. Analyze your games, get coaching feedback, understand your opening choices — right where your games and repertoire already live.") }}</p>
            </div>

            <div class="feature-card">
              <h3>{{ t("Use Them or Lose Them") }}</h3>
              <p>{{ t("Credits refresh every month and don't roll over. A good reason to keep coming back, analyze a game, and get better.") }}</p>
            </div>

          </section>
          -->

          <!--
          Play Friend — coming soon
          <section class="feature-section">
            <div class="section-header">
              <div class="section-icon-wrap purple">
                <ion-icon :icon="peopleOutline" aria-hidden="true" />
              </div>
              <h2 class="section-title">Play Friend</h2>
            </div>

            <div class="feature-card">
              <h3>{{ t("Play a Friend — Coming Soon") }}</h3>
              <p>{{ t("Play blindfold chess against a friend on the same device. Pass and play, rotate for face-to-face, or flip automatically after each move.") }}</p>
            </div>

          </section>
          -->

          <!-- Keep me programming -->
          <section class="feature-section programming-section">
            <div class="section-header">
              <div class="section-icon-wrap coral">
                <ion-icon :icon="heartOutline" aria-hidden="true" />
              </div>
              <h2 class="section-title">{{ t("Keep Me Programming!") }}</h2>
            </div>

            <div class="feature-card">
              <p>{{ t("Many subscribers appreciate the unique features within ChessVis and subscribe simply to encourage the work to continue. I won't argue with that — and I'm genuinely grateful.") }}</p>
              <p class="card-spacer">{{ t("Running ChessVis involves real ongoing costs: production and development servers, database infrastructure, automated backups, email services, and the many other expenses that come with keeping a serious app running reliably with 99.99% uptime in the past 12 months. Your subscription directly funds all of it.") }}</p>
              <p class="card-spacer">{{ t("So thank you. Every subscriber makes the next feature possible.") }}</p>
            </div>

          </section>

          <!-- Pricing -->
          <section class="pricing-section">
            <h2 class="pricing-title">{{ t("All that for only:") }}</h2>

            <div class="pricing-card pricing-highlight">
              <div class="pricing-amount">{{ annualPrice }}<span class="pricing-period">/{{ t("year") }}</span></div>
              <p class="pricing-desc">{{ t("Annual auto-renewing subscription. Best subscription value.") }}</p>
            </div>

            <div class="pricing-card">
              <div class="pricing-amount">{{ monthlyPrice }}<span class="pricing-period">/{{ t("month") }}</span></div>
              <p class="pricing-desc">{{ t("Monthly auto-renewing subscription. Try it for a month with no long-term commitment.") }}</p>
            </div>

            <div class="pricing-card">
              <div class="pricing-amount">{{ perpetualPrice }}<span class="pricing-period"> once</span></div>
              <p class="pricing-desc">{{ t("Perpetual one-time fee. Never see a recurring charge again.") }}</p>
            </div>

            <!-- <ion-button expand="block" class="subscribe-btn" @click="handleSubscribe">
              {{ t("Verify Your Email to Subscribe") }}
            </ion-button> -->

            <p class="pricing-note">{{ t("Note: If you cancel an annual or monthly subscription, you will not receive a refund for the current billing period but will continue to receive subscriber benefits until the period ends. Cancelling stops renewal — no pro-rated refunds. If you are unsure, start with a monthly subscription and evaluate from there. Terms of Service and privacy policy are available under Support in the Account section.") }}</p>
          </section>
        </div>

         <div v-show="showState == SHOW_BUTTONS">
            <ion-item>
              <ion-label class="indent-text">
                <div v-if="showRevenueCatBlurb">
                  {{ t("After clicking one of the following buttons, the device's subscription system will be appear.") }}
                </div>
                <div v-if="!showRevenueCatBlurb">
                  {{ t("After clicking one of the following buttons, you'll be taken to the secure 'Stripe.com' website. (This might take a couple of seconds.) When you're done with that page, you should automatically be back in Chessvis.") }}
              </div>
              </ion-label>
            </ion-item>
           
                  <ion-button expand="block" class="btn-perpetual" @click="doSubscribe('cp')" >
                    <span class="ion-text-wrap">{{ perpetualPrice }} {{ t("Perpetual, Lifetime Subscription") }}</span></ion-button>
             
                  <ion-button expand="block" class="btn-annual" @click="doSubscribe('ca')" >
                    <span class="ion-text-wrap">{{ annualPrice }} {{ t("Annual Subscription") }}</span></ion-button>
              
                  <ion-button expand="block" class="btn-monthly" @click="doSubscribe('cm')" >
                    <span class="ion-text-wrap">{{ monthlyPrice }} {{ t("Monthly Subscription") }}</span></ion-button>
              
                  <ion-button expand="block" class="btn-terms" @click="displayTOS()" >
                    <span class="ion-text-wrap">{{ t("Show Terms of Service and Privacy Policy") }}</span></ion-button>
              
                  <div v-if="isIOS">
                        <ion-button expand="block" class="btn-terms" @click="doRestore()" >
                    <span class="ion-text-wrap">{{ t("Restore Subscription") }}</span></ion-button>
                  </div>
          </div>

      </div>
    </ion-content>
    <Toolbar :elements="toolbarElements" @toolbar-click="toolbarClick" />
  </ion-page>
</template>

<script setup lang="ts">
import {
  IonPage, IonHeader, IonToolbar, IonTitle,
  IonButtons, IonBackButton, IonContent,
  IonIcon, IonButton,
} from '@ionic/vue'
import {
  checkmarkCircleOutline, extensionPuzzleOutline, trophyOutline,
  analyticsOutline, eyeOffOutline, heartOutline,
  // sparklesOutline, peopleOutline, // uncomment when needed
} from 'ionicons/icons'
import BottomNav from '@/components/BottomNav.vue'
import { t } from '@/composables/translateText.js'


function handleSubscribe() {
}

 import { IonLabel,IonCol, IonRow, IonGrid, IonButton, IonItem,IonInput,IonPage, IonContent, modalController } from '@ionic/vue';

  import { ref, watch , onMounted } from 'vue'
  import ModalErrorMessage from '@/components/ModalErrorMessage.vue'
  import ModalTOS from '@/components/ModalTOS.vue'
  import AlertReplacement from '@/components/AlertReplacement.vue'
  import { t } from '@/composables/translateText.js'

  import { LOG_LEVEL, Purchases, PURCHASES_ERROR_CODE } from "@revenuecat/purchases-capacitor";
  
  import { useConnectionStore } from '@/stores/connectionStore';
  import { useWindowSizeStore } from '@/stores/windowSizeStore';
  import Toolbar from '@/components/Toolbar.vue'
  import { useGetData , getWebSiteURL} from '@/composables/getData.js'
  import { doStartupCheck } from '@/composables/startupCheck.js'
  import { scrollElement } from '@/composables/screenSize'
  import {buildStyleToolbar, buildStyleTopline,buildStyleMiddle,availableWidthHeight }  from '@/composables/screenSize'

  const emit = defineEmits(['next-item-click'])

  const connectionStore = useConnectionStore()

  const whichMenu = localStorage.getItem("whichMenu")
  const showBigButtons = ref(whichMenu == "buttons" || connectionStore.getShowBigButtons)

  const deviceType = connectionStore.getDeviceType
  const isIOS = ref(deviceType == "ios")

  //console.log("DEVICE TYPE ",deviceType)

  const isDark = ref(connectionStore.getDarkmode != "" && connectionStore.getDarkmode != "light")
  const menuTrigger = ref(0)

  const topLineString = ref("")
  var isSubscriber = false
  var isDummyUser = false
  var isVerified = false

  const alertInfo = ref({})
  const alertTrigger = ref(0)

  var toolbarAction = "buttons"
 
  const priceMessage = ref("")
  const monthlyPrice = ref("MONTHLY")
  const annualPrice = ref("ANNUAL")
  const perpetualPrice = ref("PERPETUAL")
  var monthlyPriceInPennies = 0
  var annualPriceInPennies = 0
  var perpetualPriceInPennies = 0

  var buttonAlreadyClicked = false

  const iosNotSubscribedHeader = t("You are not currently logged in")
  const iosNotSubscribedMessage = t("It's not necessary to register or login to subscribe. Doing so will enable you to access the content from any of your iOS devices and desktop web browsers.")

  // RevenueCat Start

  var isRevenueCat = (deviceType == "android" || deviceType == "ios")
  const showRevenueCatBlurb = ref(isRevenueCat)
  var revenueCatAndroidAPIKey = "goog_jDcdIISldtnhRSJaRIjsYXtBsOT"
  var revenueCatIOSAPIKey = "appl_ZBeXfgZRQCplUUeIGhDlRHgDPWa"
  var revenueCatUniqueId = "" // "tK3b8M9AhqdKjt4StdI4B3AN14nOyg5g"

  var revenueCatErrorMessage = t("Hi, doing the subscriptions this way is new to me and it generated an error. PLEASE send henry@conceptual.com this message -> ")

  var monthlyPackage = null
  var annualPackage = null
  var perpetualPackage = null

  var monthlyProduct = null
  var annualProduct = null
  var perpetualProduct = null

  // RevenueCat End

  var scrollTop = 0
  
  const SHOW_BLURB = 0
  const SHOW_BUTTONS = 1

  const showState = ref(SHOW_BLURB)

  const TOOLBAR_SHOW_BUTTONS = 0
  const TOOLBAR_SCROLL_UP = 1
  const TOOLBAR_SCROLL_DOWN = 2
  const TOOLBAR_DONE = 3

  const toolbarElements = ref([
          { index : TOOLBAR_SHOW_BUTTONS, label : t("Show Subscription Options") , disabled : false, collapse:false},
        ]) 

  if (showBigButtons.value) {
    toolbarElements.value.push({ index : TOOLBAR_SCROLL_UP, label : t("Up") ,disabled : false})
    toolbarElements.value.push({ index : TOOLBAR_SCROLL_DOWN, label : t("Down") ,disabled : false})
  }

  if (whichMenu == "buttons")
    toolbarElements.value.push({ index : TOOLBAR_DONE, label : t("Done") , disabled : false})

  const windowSizeStore = useWindowSizeStore()
  const windowSizeRef = ref({ width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] })

   const styleSubscribe = ref({
    position : "absolute",
    "overflow-y" : "scroll",
    top : "0px",
    left : "100px",
    width: "100px",
    height : "100px"
  })

  setButtonText()

  doStartupCheck(false,afterStartupCallbackRoutine)

  const revenueCatGetProducts = async () => {
    try {
      const offerings = await Purchases.getOfferings();
      if (offerings.current !== null && offerings.current.availablePackages.length !== 0) { 
        monthlyPackage = offerings.current.monthly
        annualPackage = offerings.current.annual
        perpetualPackage = offerings.current.lifetime
        monthlyProduct = monthlyPackage.product
        annualProduct = annualPackage.product
        perpetualProduct = perpetualPackage.product
        monthlyPrice.value = monthlyProduct.priceString
        annualPrice.value = annualProduct.priceString
        perpetualPrice.value = perpetualProduct.priceString
      }
      else
        alert(revenueCatErrorMessage + "revenue cat no offerings");
    } catch (error) {
      alert(revenueCatErrorMessage + "revenue cat get products error" + error);
      // Handle error
    }
  }

  const onDeviceReady = async () => {
    await Purchases.setLogLevel(LOG_LEVEL.DEBUG);
    if (deviceType === 'ios') {
        //console.log("**** FOR IOS")
        await Purchases.configure({ apiKey: revenueCatIOSAPIKey, appUserID : revenueCatUniqueId });
    } else if (deviceType === 'android') {
        //console.log("**** FOR ANDROID")
        await Purchases.configure({ apiKey: revenueCatAndroidAPIKey , 
                      appUserID: revenueCatUniqueId });
    }
  }

  onMounted(() => {
    if (!isRevenueCat) {
      getStripePricing()
      return;
    }

    getRevenueUniqueId()
   })

  watch(() => windowSizeStore.getWindowSize, () => {
    //console.log('*********** window size changes detected')
    windowSizeRef.value = { width : windowSizeStore.getWindowSize['width'],
                      height : windowSizeStore.getWindowSize['height'] }
    //console.log("*** logoff new window size",windowSizeRef.value)
    menuTrigger.value++
    updateWidthHeights()
  })
  
  function setupAlert(header,message,type,task) {
    //console.log("in setup alert")
    alertInfo.value = { header : header, message : message, type : type, task : task  }
    alertTrigger.value++
  }

  function setButtonText() {
    isSubscriber = (connectionStore.getIsSubscriber == 1)
    isDummyUser = (connectionStore.getDummy != 0)
    isVerified = (connectionStore.getEmailVerified == "y" || connectionStore.getEmailVerified == "yes")

    //console.log("in set buttons",deviceType)
    // apple doesn't require login but we can ask
    if (deviceType == "ios" && isDummyUser) {
      setupAlert(iosNotSubscribedHeader,iosNotSubscribedMessage,"alert","ios")
      return
    }

    if (isSubscriber) {
      topLineString.value = t("Thank you for being a subscriber")
      if (!isVerified) {
          toolbarElements.value[0].label = t("Take me to the 'Email Verify' screen")
          toolbarAction = "verify"
      }
      else
        toolbarElements.value[0].label = t("Review all Subscription Options")
    }
    else
      if (isDummyUser) {
        topLineString.value = t("Please signin/register to sync across all devices")
        toolbarElements.value[0].label = t("Take me to the 'Signin' screen")
        toolbarAction = "signin"
      }
      else
      if (isVerified) {
        topLineString.value = t("Subscribing has lots of benefits")
        toolbarElements.value[0].label = t("YES! I want to subscribe!")
      }
      else {
        topLineString.value = t("Verify your email to subscribe")
        toolbarElements.value[0].label = t("Take me to the 'Email Verify' screen")
        toolbarAction = "verify"
      }
  }

  function updateWidthHeights() {
    const availableSpace = availableWidthHeight()
    const subscribePositionTop = availableSpace.topIndent
    const subscribePositionLeft = availableSpace.leftRightIndent + availableSpace.leftIndent
    const subscribeHeight = availableSpace.height - subscribePositionTop + availableSpace.topIndent
    const subscribeWidth = availableSpace.width - availableSpace.leftRightIndent * 2

    styleSubscribe.value.height = subscribeHeight + "px"
    styleSubscribe.value.width = subscribeWidth + "px"
    styleSubscribe.value.top = subscribePositionTop + "px"
    styleSubscribe.value.left = subscribePositionLeft + "px"
  }

  function processStripeCheckoutResponse(json) {
    //console.log("in processStripeCheckoutResponse callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }
    const url = json['url']

    // stripe can't run inside an iframe.
    if (window.top !== window.self) {
      // The page is inside an iframe.
      console.log("This page is inside an iframe.");
      window.open(url, '_blank');
      window.open(url);
    } 
    else {
      // The page is not inside an iframe (it's the top-level window).
      console.log("This page is not inside an iframe.");
      location.href = url
    }

  }

  function processJsonResponse (json) {
    buttonAlreadyClicked = false
    //console.log(" in callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    const product = json['p']
    const code = json['c']

    var formData = { 
      p : product,
      c : code  
    }
    //console.log("formData",formData)
    useGetData("chessvis_stripe_checkout.php", formData, processStripeCheckoutResponse)
  }

  function doRevenueCatSubscribe(whatOrder) {
    var revenueCatPackage = null
    switch (whatOrder) {
      case "cp" :
        revenueCatPackage = perpetualPackage
        break
      case "cm" :
        revenueCatPackage = monthlyPackage
        break
      case "ca" :
        revenueCatPackage = annualPackage
        break
    }
    makeRevenueCatSubscription(revenueCatPackage)
  }

  const makeRevenueCatSubscription = async (revenueCatPackage) => {
    try {
      const purchaseResult = await Purchases.purchasePackage({ aPackage: revenueCatPackage });
      if (typeof purchaseResult.customerInfo.entitlements.active['my_entitlement_identifier'] !== "undefined") {
        // Unlock that great "pro" content
        alert(t("Thank you for subscribing!!"))
        //alert("Purchase Success")
      }
    } catch (error) {
      if (error.code === PURCHASES_ERROR_CODE.PURCHASE_CANCELLED_ERROR) {
        // Purchase cancelled
        alert(t("Purchase Cancelled"))
      } else {
        // Error making purchase
        alert(revenueCatErrorMessage + t("Purchase ERROR"))
      }
    }
  }

  function displayTOS() {
    openTOSModal()

  }

  function doSubscribe(whatOrder) {

    if (isRevenueCat) {
      doRevenueCatSubscribe(whatOrder)
      return
    }

    if (buttonAlreadyClicked)
      return

    buttonAlreadyClicked = true
    //console.log("Stripe what ordered",whatOrder)

    var formData = { p : whatOrder  }
    useGetData("chessvis_stripe_code.php", formData, processJsonResponse)

  }

  // this is only on page with buttons
  function toolbarBackClick(arrayIndex) {
    doStartupCheck(true,afterStartupCallbackRoutine)
    showState.value = SHOW_BLURB
  }

  function toolbarClick(arrayIndex) {

    switch (arrayIndex) {
      case TOOLBAR_DONE :
        emit("next-item-click","big_button_menu",t("Big Button Menu"),"buttons")
        return
        break
      case TOOLBAR_SCROLL_DOWN :
        scrollElement("page","down")
        return
        break
      case TOOLBAR_SCROLL_UP :
        scrollElement("page","up")
        return
        break
    }

    if (showState.value == SHOW_BUTTONS) {
      toolbarElements.value[0].label = t("Review all Subscription Options")
      showState.value = SHOW_BLURB
      return
    }

    switch (toolbarAction) {
      case "verify" :
        emit("next-item-click","verify_email","Verify Email Address","subscribe")
        break
      case "signin" :
        emit("next-item-click","signin","Signin","subscribe")
        break
      case "buttons" :
        toolbarElements.value[0].label = t("Return to the Description")
        showState.value = SHOW_BUTTONS
        break
    }
  }

  const openErrorModal = async (programName,errorMessage) => {
    const modal = await modalController.create({
      component: ModalErrorMessage,
      componentProps: {
        type : "commError",
        program : programName ,
        errorMessage : errorMessage
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  const openTOSModal = async () => {
    const modal = await modalController.create({
      component: ModalTOS,
      componentProps: {
      }
    });

    modal.present();

    const { data, role } = await modal.onWillDismiss();
  };

  function afterStartupCallbackRoutine(stuffChanged,errorProgram,errorEntry) {
    if (errorProgram != null)
      openErrorModal(errorProgram,errorEntry)
    
    setButtonText()
    menuTrigger.value++
    updateWidthHeights()
  }

  function processStripePricingJsonResponse(json) {
    //console.log("*** in get stripe pricing json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    annualPriceInPennies = json['a']
    monthlyPriceInPennies = json['m']
    perpetualPriceInPennies = json['p']

    annualPrice.value = "$" + (parseFloat(annualPriceInPennies)/100).toFixed(2)
    monthlyPrice.value = "$" + (parseFloat(monthlyPriceInPennies)/100).toFixed(2)
    perpetualPrice.value = "$" + (parseFloat(perpetualPriceInPennies)/100).toFixed(2)
  }

  function getStripePricing() {
    var formData = { product : "chessvis"  }

    //console.log("in get stripe Pricing",formData)
    useGetData("chessvis_stripe_pricing.php", formData, processStripePricingJsonResponse)
  }

  function doRestore() {
    makeRevenueCatRestore()
  }

  const makeRevenueCatRestore = async () => {

    try {
      const res = await Purchases.restorePurchases();
      alert("Subscription Restored")
      //console.log("RES",res)
    } catch (error) {
      alert(revenueCatErrorMessage + "revenue cat restore error" + error);
      // Handle error
    }
  }

  function processRevenueCatIdJsonResponse(json) {
    //console.log("*** in revenue cat json callback",json)
    if (json.hasOwnProperty('error')) {
      openErrorModal(json['programRun'],json['error'])
      return;
    }

    revenueCatUniqueId = json['id']

    // if (deviceType == "ios") {
    //   //alert("IOS not in revenuecat yet")
    //   return
    // }

    // onDeviceReady instead of configure
       onDeviceReady().then(() => { 
        //alert("RevenueCat SDK configured!") 
        console.log("Revenuecat SDK configured")
        revenueCatGetProducts()
      })
    .catch(error => {
      alert(revenueCatErrorMessage + " RevenueCat SDK configuration error");
    })
  }

  function getRevenueUniqueId() {
    var formData = { p : "chessvis"  }

    //console.log("in get revenuecat id",formData)
    useGetData("chessvis_revenuecat_info.php", formData, processRevenueCatIdJsonResponse)
  }

</script>

<style scoped>
ion-toolbar {
  --background: #12122a;
  --color: #ffffff;
  --border-color: #2a2a4a;
}

ion-back-button { --color: #AFA9EC; }

ion-title {
  font-size: 17px;
  font-weight: 500;
}

ion-content { --background: #1a1a2e; }

/* ── Hero ── */
.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 32px 20px 24px;
  border-bottom: 0.5px solid #2a2a4a;
  background: linear-gradient(180deg, #1a1a35 0%, #1a1a2e 100%);
}

.top-line-note {
  font-size: 22px;
  font-weight: 700;
  color: red;
  line-height: 1.3;
}

.hero-title {
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 10px;
  line-height: 1.3;
}

.hero-sub {
  font-size: 14px;
  color: ##8c6d8;
  line-height: 1.6;
  margin: 0 0 20px;
}

.hero-cta {
  --background: #534AB7;
  --background-activated: #3C3489;
  --color: #ffffff;
  --border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  height: 52px;
  width: 100%;
}

/* ── Feature sections ── */
.feature-section {
  padding: 24px 16px 8px;
  border-bottom: 0.5px solid #2a2a4a;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.free-header .section-title { color: #1D9E75; }
.subscriber-header .section-title { color: #7F77DD; }

.section-icon-wrap {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.section-icon-wrap.teal   { background: #085041; color: #9FE1CB; }
.section-icon-wrap.purple { background: #3C3489; color: #CECBF6; }
.section-icon-wrap.coral  { background: #712B13; color: #F5C4B3; }
.section-icon-wrap.ai     { background: #2d2060; color: #c4bfff; }

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
}

/* ── Benefits list ── */
.benefits-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0 8px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #c8c6d8;
}

.benefit-icon {
  font-size: 20px;
  color: #7F77DD;
  flex-shrink: 0;
}

/* ── Feature cards ── */
.feature-card {
  background: #1e1e35;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 10px;
  border: 0.5px solid #2a2a4a;
}

.feature-card.highlight {
  border-color: #534AB7;
  background: #1a1535;
}

.feature-card-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7F77DD;
  margin-bottom: 6px;
}

.feature-card h3 {
  font-size: 15px;
  font-weight: 600;
  color: #e0e0f0;
  margin: 0 0 6px;
}

.feature-card p {
  font-size: 13px;
  color: #c8c6d8;
  line-height: 1.6;
  margin: 0;
}

.card-spacer {
  margin-top: 10px !important;
}

/* ── Keep me programming ── */
.programming-section .feature-card {
  border-color: #712B13;
  background: #1f0f0a;
}

/* ── Pricing ── */
.pricing-section {
  padding: 24px 16px 40px;
}

.pricing-title {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 16px;
  text-align: center;
}

.pricing-card {
  background: #1e1e35;
  border-radius: 12px;
  padding: 16px 20px;
  margin-bottom: 10px;
  border: 0.5px solid #2a2a4a;
  text-align: center;
}

.pricing-card.pricing-highlight {
  border-color: #534AB7;
  background: #1a1535;
}

.pricing-amount {
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 6px;
}

.pricing-period {
  font-size: 16px;
  font-weight: 400;
  color: #AFA9EC;
}

.pricing-desc {
  font-size: 13px;
  color: #c8c6d8;
  line-height: 1.5;
  margin: 0;
}

.subscribe-btn {
  --background: #534AB7;
  --background-activated: #3C3489;
  --color: #ffffff;
  --border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  height: 52px;
  margin: 20px 0 16px;
}

.pricing-note {
  font-size: 11px;
  color: #c8c6d8;
  line-height: 1.6;
  margin: 0;
  font-style: italic;
}

/* Page background */
.payment-page {
  background: #1a1a2e;
  padding: 20px 16px;
}

/* Intro text */
.payment-intro {
  font-size: 13px;
  color: #aaa8c0;
  line-height: 1.6;
  margin-bottom: 24px;
  text-align: center;
}

/* All buttons full width, stacked */
.payment-btn {
  display: block;
  width: 100%;
  padding: 16px;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 12px;
  cursor: pointer;
  border: none;
}

/* Perpetual — most prominent */
.btn-perpetual {
  --background: #534AB7;
  --background-activated: #3C3489;
  --color: #ffffff;
  --border-radius: 14px;
  margin-bottom: 12px;
  width: 100%;
}

.btn-annual {
  --background: #1D9E75;
  --background-activated: #168a64;
  --color: #ffffff;
  --border-radius: 14px;
  margin-bottom: 12px;
  width: 100%;
}

.btn-monthly {
  --background: #1e1e35;
  --background-activated: #2a2a45;
  --color: #e0e0f0;
  --border-radius: 14px;
  --border-width: 0.5px;
  --border-style: solid;
  --border-color: #2a2a4a;
  margin-bottom: 12px;
  width: 100%;
}

.btn-terms {
  --background: transparent;
  --background-activated: transparent;
  --color: #7F77DD;
  --border-radius: 14px;
  --border-width: 0.5px;
  --border-style: solid;
  --border-color: #534AB7;
  margin-bottom: 12px;
  width: 100%;
  font-size: 13px;
}

.btn-perpetual, .btn-annual, .btn-monthly, .btn-terms {
  --padding-top: 18px;
  --padding-bottom: 18px;
  height: auto;
}

</style>
